/**
 * ReporterProfile - Instagram-style user profile
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/utils/cn';
import { useSocialStore } from '@/stores/useSocialStore';
import { useAuthStore } from '@/stores';
import { ReportCard } from './ReportCard';

interface ReporterProfileProps {
  userId: string;
  onBack?: () => void;
  onReportClick?: (reportId: string) => void;
}

export function ReporterProfile({ userId, onBack, onReportClick }: ReporterProfileProps) {
  const { viewingProfile, profileReports, fetchProfile, followUser, unfollowUser } = useSocialStore();
  const { user } = useAuthStore();
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [activeTab, setActiveTab] = useState<'reports' | 'likes'>('reports');

  const isOwnProfile = user?.id === userId;

  useEffect(() => {
    fetchProfile(userId);
  }, [userId, fetchProfile]);

  useEffect(() => {
    if (viewingProfile) {
      setIsFollowing(viewingProfile.is_following || false);
      setFollowersCount(viewingProfile.followers_count);
    }
  }, [viewingProfile]);

  const handleFollow = async () => {
    if (isFollowing) {
      setIsFollowing(false);
      setFollowersCount((c) => c - 1);
      await unfollowUser(userId);
    } else {
      setIsFollowing(true);
      setFollowersCount((c) => c + 1);
      await followUser(userId);
    }
  };

  if (!viewingProfile) {
    return (
      <div className="flex items-center justify-center h-64">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-8 h-8 border-2 border-hud-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-app-bg/80 backdrop-blur-xl border-b border-glass-border">
        <div className="flex items-center gap-4 p-4">
          {onBack && (
            <button onClick={onBack} className="text-white hover:text-hud-primary">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white">{viewingProfile.username}</h1>
            <span className="text-xs text-hud-primary font-mono">{viewingProfile.reporter_id}</span>
          </div>
        </div>
      </div>

      {/* Profile info */}
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="relative">
            {viewingProfile.avatar_url ? (
              <img
                src={viewingProfile.avatar_url}
                alt={viewingProfile.username}
                className="w-20 h-20 rounded-full object-cover border-2 border-hud-primary"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-hud-primary to-teal-600 flex items-center justify-center border-2 border-hud-primary">
                <span className="text-2xl font-bold text-white">
                  {viewingProfile.username[0].toUpperCase()}
                </span>
              </div>
            )}
            {viewingProfile.is_verified_reporter && (
              <span className="absolute -bottom-1 -right-1 text-xl">✅</span>
            )}
          </div>

          {/* Stats */}
          <div className="flex-1 grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-xl font-bold text-white">{viewingProfile.reports_count}</div>
              <div className="text-xs text-text-secondary">Reports</div>
            </div>
            <div>
              <div className="text-xl font-bold text-white">{followersCount}</div>
              <div className="text-xs text-text-secondary">Followers</div>
            </div>
            <div>
              <div className="text-xl font-bold text-white">{viewingProfile.following_count}</div>
              <div className="text-xs text-text-secondary">Following</div>
            </div>
          </div>
        </div>

        {/* Name & bio */}
        <div className="mt-4">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-white">
              {viewingProfile.first_name} {viewingProfile.last_name}
            </span>
            {viewingProfile.subscription_tier === 'pro' && (
              <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">PRO</span>
            )}
            {viewingProfile.subscription_tier === 'enterprise' && (
              <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">ENTERPRISE</span>
            )}
          </div>
          {viewingProfile.bio && (
            <p className="text-text-secondary text-sm mt-1">{viewingProfile.bio}</p>
          )}
        </div>

        {/* Badges */}
        {viewingProfile.badges && viewingProfile.badges.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {viewingProfile.badges.map((ub: any) => (
              <span
                key={ub.badge?.id}
                className="text-xs bg-slate-700/50 px-2 py-1 rounded-full"
                title={ub.badge?.description}
              >
                {ub.badge?.icon} {ub.badge?.name}
              </span>
            ))}
          </div>
        )}

        {/* Action buttons */}
        <div className="mt-4 flex gap-2">
          {isOwnProfile ? (
            <button className={cn(
              'flex-1 py-2.5 rounded-xl font-semibold text-sm',
              'bg-glass-bg border border-glass-border text-white',
              'hover:bg-glass-border transition-colors'
            )}>
              Edit Profile
            </button>
          ) : (
            <>
              <button
                onClick={handleFollow}
                className={cn(
                  'flex-1 py-2.5 rounded-xl font-semibold text-sm transition-colors',
                  isFollowing
                    ? 'bg-glass-bg border border-glass-border text-white hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/30'
                    : 'bg-hud-primary text-black hover:bg-hud-primary/80'
                )}
              >
                {isFollowing ? 'Following' : 'Follow'}
              </button>
              <button className={cn(
                'px-4 py-2.5 rounded-xl font-semibold text-sm',
                'bg-glass-bg border border-glass-border text-white',
                'hover:bg-glass-border transition-colors'
              )}>
                Message
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-glass-border">
        <div className="flex">
          {[
            { key: 'reports', label: '📝 Reports' },
            { key: 'likes', label: '❤️ Likes' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              className={cn(
                'flex-1 py-3 text-sm font-medium transition-colors relative',
                activeTab === tab.key ? 'text-white' : 'text-text-secondary hover:text-white'
              )}
            >
              {tab.label}
              {activeTab === tab.key && (
                <motion.div
                  layoutId="profileTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-hud-primary"
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Reports grid */}
      <div className="p-4 space-y-4">
        {profileReports.length === 0 ? (
          <div className="py-16 text-center">
            <div className="text-4xl mb-4">📝</div>
            <h3 className="text-lg font-semibold text-white mb-2">No reports yet</h3>
            <p className="text-text-secondary">
              {isOwnProfile ? "You haven't filed any reports yet" : "This reporter hasn't filed any reports yet"}
            </p>
          </div>
        ) : (
          profileReports.map((report) => (
            <ReportCard
              key={report.id}
              report={{ ...report, user: viewingProfile }}
              onReportClick={onReportClick}
            />
          ))
        )}
      </div>
    </div>
  );
}
